/**
 * Generic Notification Service
 * Supports both WebSocket and Firebase with easy configuration switching
 */

// Configuration
const NOTIFICATION_CONFIG = {
  provider: process.env.REACT_APP_NOTIFICATION_PROVIDER || 'websocket', // 'websocket' | 'firebase' | 'both'
  
  // WebSocket config
  websocket: {
    url: process.env.REACT_APP_WS_URL || 'ws://localhost:8000/api/v1/notifications/ws',
    reconnectInterval: 3000,
    maxReconnectAttempts: 5
  },
  
  // Firebase config (optional)
  firebase: {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || null,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || null,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || null,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || null,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || null,
    appId: process.env.REACT_APP_FIREBASE_APP_ID || null,
    vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY || null
  }
};

// Base Notification Service Interface
class NotificationServiceInterface {
  async initialize() { throw new Error('Not implemented'); }
  async subscribe(userId, callback) { throw new Error('Not implemented'); }
  async unsubscribe(userId) { throw new Error('Not implemented'); }
  async requestPermission() { throw new Error('Not implemented'); }
}

// WebSocket Implementation
class WebSocketNotificationService extends NotificationServiceInterface {
  constructor() {
    super();
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = NOTIFICATION_CONFIG.websocket.maxReconnectAttempts;
    this.notificationCallback = null;
    this.userId = null; // Store userId for reconnection
    this.keepAliveInterval = null; // Keep-alive interval
  }

  async initialize() {
    // WebSocket initialization is deferred until subscribe() is called
    // This allows the service to be initialized before user authentication is complete
    return Promise.resolve();
  }

  async subscribe(userId, callback, connectionStatusCallback = null) {
    this.notificationCallback = callback;
    this.connectionStatusCallback = connectionStatusCallback;
    this.userId = userId; // Store userId for reconnection
    
    // Create WebSocket connection when subscribing
    return new Promise((resolve, reject) => {
      const { user, accessToken } = this.getAuthData();
      if (!user || !accessToken) {
        reject(new Error('User not authenticated'));
        return;
      }

      const wsUrl = `${NOTIFICATION_CONFIG.websocket.url}/${userId}?token=${encodeURIComponent(accessToken)}`;
      console.log('Attempting WebSocket connection to:', wsUrl);
      this.socket = new WebSocket(wsUrl);
      
      this.socket.onopen = () => {
        console.log('WebSocket connected successfully');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        
        // Notify connection status callback
        if (this.connectionStatusCallback) {
          console.log('WebSocket: Calling connectionStatusCallback(true)');
          this.connectionStatusCallback(true);
        }
        
        // Start keep-alive mechanism
        this.startKeepAlive();
        
        resolve();
      };

      this.socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification') {
            this.handleNotification(data.data);
          } else if (data.type === 'pong') {
            // Keep-alive response, no action needed
            console.log('WebSocket keep-alive received');
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.socket.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        this.isConnected = false;
        
        // Handle authentication errors (don't attempt to reconnect)
        if (event.code === 1008) { // Policy violation (authentication failed)
          console.error('WebSocket authentication failed:', event.reason);
          // Don't attempt to reconnect on auth failure
          if (this.connectionStatusCallback) {
            this.connectionStatusCallback(false);
          }
          reject(new Error(`Authentication failed: ${event.reason}`));
          return;
        }
        
        // Notify connection status callback
        if (this.connectionStatusCallback) {
          console.log('WebSocket: Calling connectionStatusCallback(false)');
          this.connectionStatusCallback(false);
        }
        
        this.handleReconnect();
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        console.error('WebSocket readyState:', this.socket.readyState);
        this.isConnected = false;
        
        // Notify connection status callback
        if (this.connectionStatusCallback) {
          console.log('WebSocket: Calling connectionStatusCallback(false) due to error');
          this.connectionStatusCallback(false);
        }
        
        reject(error);
      };
    });
  }

  async unsubscribe(userId) {
    if (this.socket) {
      this.socket.close();
    }
    this.stopKeepAlive();
    this.userId = null;
    this.notificationCallback = null;
  }

  startKeepAlive() {
    this.stopKeepAlive(); // Clear any existing interval
    this.keepAliveInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
      }
    }, 30000); // Send ping every 30 seconds
  }

  stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  async requestPermission() {
    // WebSocket doesn't need permission
    return true;
  }

  handleNotification(data) {
    if (this.notificationCallback) {
      this.notificationCallback(data);
    }
  }

  handleReconnect() {
    // Check if user is still authenticated before attempting reconnection
    const { user, accessToken } = this.getAuthData();
    if (!user || !accessToken) {
      console.log('User no longer authenticated, skipping reconnection');
      return;
    }
    
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.userId) {
      this.reconnectAttempts++;
      setTimeout(() => {
        console.log(`Reconnecting... attempt ${this.reconnectAttempts}`);
        this.subscribe(this.userId, this.notificationCallback).catch(console.error);
      }, NOTIFICATION_CONFIG.websocket.reconnectInterval * this.reconnectAttempts);
    }
  }

  getAuthData() {
    // Get from localStorage using the same key as AuthContext
    const STORAGE_KEY = "app_auth_state";
    const authState = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const user = authState.user || {};
    const accessToken = authState.accessToken;
    console.log('Auth data retrieved:', { 
      user: user ? { user_id: user.user_id, username: user.username } : null, 
      hasToken: !!accessToken 
    });
    return { user, accessToken };
  }
}

// Firebase Implementation
class FirebaseNotificationService extends NotificationServiceInterface {
  constructor() {
    super();
    this.messaging = null;
    this.isInitialized = false;
    this.notificationCallback = null;
  }

  async initialize() {
    if (this.isInitialized) return;

    try {
      // Check if Firebase configuration is available
      const firebaseConfig = NOTIFICATION_CONFIG.firebase;
      if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
        console.warn('Firebase configuration not found - notifications will use WebSocket only');
        this.isInitialized = true;
        return;
      }

      // Check if Firebase is available
      if (typeof window === 'undefined') {
        console.warn('Firebase not available in server environment - notifications will use WebSocket only');
        this.isInitialized = true;
        return;
      }

      const { getMessaging } = await import('firebase/messaging');
      const { initializeApp } = await import('firebase/app');

      const app = initializeApp(firebaseConfig);
      this.messaging = getMessaging(app);
      
      // Register service worker
      await this.registerServiceWorker();
      
      this.isInitialized = true;
      console.log('Firebase initialized successfully');
    } catch (error) {
      console.warn('Firebase initialization failed - falling back to WebSocket only:', error);
      this.isInitialized = true; // Mark as initialized to prevent retries
    }
  }

  async requestPermission() {
    if (!this.messaging) {
      console.warn('Firebase not available - skipping permission request');
      return null;
    }
    
    try {
      const { getToken } = await import('firebase/messaging');
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const token = await getToken(this.messaging, {
          vapidKey: NOTIFICATION_CONFIG.firebase.vapidKey
        });
        
        // Send token to backend
        await this.registerToken(token);
        return token;
      }
      return null;
    } catch (error) {
      console.warn('Firebase permission request failed:', error);
      return null;
    }
  }

  async subscribe(userId, callback) {
    if (!this.messaging) {
      console.warn('Firebase not available - skipping subscription');
      return;
    }
    
    try {
      this.notificationCallback = callback;
      
      // Listen for foreground messages
      const { onMessage } = await import('firebase/messaging');
      onMessage(this.messaging, (payload) => {
        this.handleNotification(payload.notification);
      });
    } catch (error) {
      console.warn('Firebase subscription failed:', error);
    }
  }

  async unsubscribe(userId) {
    // Firebase doesn't need explicit unsubscribe
  }

  async registerServiceWorker() {
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('Service worker registered:', registration);
      } catch (error) {
        console.warn('Service worker registration failed:', error);
      }
    }
  }

  async registerToken(token) {
    try {
      const response = await fetch('/api/notifications/fcm-token', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('app_auth_state') || '{}').accessToken}`
        },
        body: JSON.stringify({ 
          fcm_token: token,
          device_type: 'web'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to register FCM token');
      }
      
      console.log('FCM token registered successfully');
    } catch (error) {
      console.error('FCM token registration failed:', error);
    }
  }

  handleNotification(notification) {
    if (this.notificationCallback) {
      this.notificationCallback({
        title: notification.title,
        message: notification.body,
        type: 'info',
        created_at: new Date().toISOString()
      });
    }
  }
}

// Combined Service (Both WebSocket and Firebase)
class CombinedNotificationService extends NotificationServiceInterface {
  constructor() {
    super();
    this.websocketService = new WebSocketNotificationService();
    this.firebaseService = new FirebaseNotificationService();
    this.notificationCallback = null;
  }

  async initialize() {
    try {
      // Initialize WebSocket service (required)
      await this.websocketService.initialize();
      
      // Initialize Firebase service (optional)
      try {
        await this.firebaseService.initialize();
        console.log('Combined notification service initialized with WebSocket + Firebase');
      } catch (firebaseError) {
        console.warn('Firebase initialization failed, using WebSocket only:', firebaseError);
        console.log('Combined notification service initialized with WebSocket only');
      }
    } catch (error) {
      console.error('Combined service initialization error:', error);
      throw error;
    }
  }

  async subscribe(userId, callback) {
    this.notificationCallback = callback;
    
    // Subscribe to WebSocket service (required)
    await this.websocketService.subscribe(userId, callback);
    
    // Subscribe to Firebase service (optional)
    try {
      await this.firebaseService.subscribe(userId, callback);
    } catch (error) {
      console.warn('Firebase subscription failed, continuing with WebSocket only:', error);
    }
  }

  async unsubscribe(userId) {
    await this.websocketService.unsubscribe(userId);
    try {
      await this.firebaseService.unsubscribe(userId);
    } catch (error) {
      console.warn('Firebase unsubscribe failed:', error);
    }
  }

  async requestPermission() {
    // Request permission for WebSocket service (always works)
    const websocketResult = await this.websocketService.requestPermission();
    
    // Request permission for Firebase service (optional)
    let firebaseResult = null;
    try {
      firebaseResult = await this.firebaseService.requestPermission();
    } catch (error) {
      console.warn('Firebase permission request failed:', error);
    }
    
    return { websocket: websocketResult, firebase: firebaseResult };
  }
}

// Factory Pattern for Easy Switching
class NotificationServiceFactory {
  static create(type = NOTIFICATION_CONFIG.provider) {
    switch (type) {
      case 'websocket':
        return new WebSocketNotificationService();
      case 'firebase':
        return new FirebaseNotificationService();
      case 'both':
        return new CombinedNotificationService();
      default:
        throw new Error(`Unknown notification service type: ${type}`);
    }
  }
}

export { 
  NotificationServiceFactory, 
  WebSocketNotificationService, 
  FirebaseNotificationService,
  CombinedNotificationService,
  NOTIFICATION_CONFIG
};
